import { useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import { Store } from 'redux';
import {
  CallResponseGetAspectForApp,
  getAspectForAppCall,
} from '@/aspect/state/api/getAspectForAppCall';
import {
  getOrganizationConfigCall,
  getOrganizationsCall,
  getUserAuthorizeCall,
  getUserProfileCall,
  getUserWarningsCall,
  postOrganizationConfigCall,
} from '@/core/state/api';
import { UserStatusEnum } from '@/core/state/types';
import { getEmployerCreditsCall } from '@/credits/state';
import { getAuthSettingsCall, useLogin } from '@/login/state';
import { getEmployeesMeCall } from '@/profile/state';
import { getEmployerNotCompletedSurveysCall, getTemplatesCall } from '@/surveys/state/api';
import { ErrorCodes, PLATFORM_TYPE_FROM_ROLE, PlatformType } from '../constants';
import { getQueryParam } from '../helpers';

export const useInit = () => {
  const idpAlias = getQueryParam();
  const [customError, setCustomError] = useState<string>();
  const store = useStore();

  const token = new URLSearchParams(window.location.search).get('token');
  const invitationId = new URLSearchParams(window.location.search).get('invitationId');

  const {
    execute: executeGetAuthSettings,
    success: successGetAuthSettings,
    error: errorGetAuthSettings,
    loading: loadingGetAuthSettings,
  } = getAuthSettingsCall.hook();
  const { isUserLogged } = useLogin();
  const {
    success: successGetUserProfile,
    execute: executeGetUserProfile,
    error: errorGetUserProfile,
    loading: loadingGetUserProfile,
    userRole,
  } = getUserProfileCall.hook();
  const {
    execute: executeGetEmployerNotCompletedSurveys,
    loading: loadingGetEmployerNotCompletedSurveys,
  } = getEmployerNotCompletedSurveysCall.hook();
  const {
    execute: executeGetOrganizationConfig,
    loading: loadingGetOrganizationConfig,
    success: successGetOrganizationConfig,
  } = getOrganizationConfigCall.hook();
  const {
    execute: executePostOrganizationConfig,
    loading: loadingPostOrganizationConfig,
    success: successPostOrganizationConfig,
  } = postOrganizationConfigCall.hook();
  const { execute: executeGetTemplates } = getTemplatesCall.hook();
  const {
    success: successGetOrganizations,
    execute: executeGetOrganizations,
    error: errorGetOrganizations,
    loading: loadingGetOrganizations,
    defaultOrganization,
  } = getOrganizationsCall.hook();
  const { execute: executeGetAspectForApp } = getAspectForAppCall.hook();
  const { execute: executeGetEmployerCredits } = getEmployerCreditsCall.hook();
  const { execute: executeGetEmployeesMe } = getEmployeesMeCall.hook();
  const { execute: executeGetUserWarnings } = getUserWarningsCall.hook();
  const { execute: executeGetUserAuthorize } = getUserAuthorizeCall.hook();

  const isSuccess = useMemo(() => {
    if (isUserLogged && defaultOrganization !== '') {
      const isLearning =
        !!userRole && PLATFORM_TYPE_FROM_ROLE[userRole as never] === PlatformType.LEARNING;
      const baseSuccess =
        successGetAuthSettings && successGetUserProfile && successGetOrganizations;
      // For non-LEARNING roles we also wait for the organization config response
      // (success of GET, or success of POST fallback when GET errors) so that
      // routing guards can rely on `hasViewConfig` before mounting.
      return (
        baseSuccess && (isLearning || successGetOrganizationConfig || successPostOrganizationConfig)
      );
    }
    if (isUserLogged && defaultOrganization === '') {
      return successGetAuthSettings && successGetOrganizations;
    }
    return successGetAuthSettings;
  }, [
    defaultOrganization,
    isUserLogged,
    successGetAuthSettings,
    successGetOrganizations,
    successGetUserProfile,
    successGetOrganizationConfig,
    successPostOrganizationConfig,
    userRole,
  ]);

  const isLoading = useMemo(() => {
    if (isUserLogged) {
      return (
        loadingGetAuthSettings ||
        loadingGetUserProfile ||
        loadingGetOrganizations ||
        loadingGetOrganizationConfig ||
        loadingPostOrganizationConfig ||
        loadingGetEmployerNotCompletedSurveys
      );
    }
    return loadingGetAuthSettings;
  }, [
    isUserLogged,
    loadingGetAuthSettings,
    loadingGetOrganizations,
    loadingGetUserProfile,
    loadingGetOrganizationConfig,
    loadingPostOrganizationConfig,
    loadingGetEmployerNotCompletedSurveys,
  ]);

  const isError = errorGetAuthSettings || errorGetUserProfile || errorGetOrganizations;

  useEffect(() => {
    if (isError && Object.keys(ErrorCodes).includes(isError?.title)) {
      throw new Error(isError.title);
    }
    if (customError && Object.keys(ErrorCodes).includes(customError)) {
      throw new Error(customError);
    }
  }, [isError, customError]);

  useEffect(() => {
    executeGetAuthSettings({
      idpAlias: idpAlias ?? undefined,
      successSaga: () => {
        if (isUserLogged) {
          executeGetOrganizations({
            successSaga: ({ response: responseOrganizations }) => {
              if (responseOrganizations?.data?.length > 0) {
                executeGetUserProfile({
                  successSaga: ({ response: responseFromGetUserProfile, selectors }) => {
                    if (responseFromGetUserProfile?.status === UserStatusEnum.Disabled) {
                      setCustomError(ErrorCodes.USER_DISABLED);
                    }
                    if (responseFromGetUserProfile?.status === UserStatusEnum.Pending) {
                      setCustomError(ErrorCodes.USER_PENDING);
                    }
                    const currentUserRole: ReturnType<
                      (typeof getUserProfileCall)['hook']
                    >['userRole'] = selectors.userRole(store.getState() as Store);
                    window.localStorage.setItem('currentRole', currentUserRole || '');
                    const userActiveContext: ReturnType<
                      (typeof getUserProfileCall)['hook']
                    >['activeContext'] = selectors.activeContext(store.getState() as Store);
                    window.localStorage.setItem('activeContext', userActiveContext || '');
                    if (
                      currentUserRole &&
                      PLATFORM_TYPE_FROM_ROLE[currentUserRole as never] === PlatformType.LEARNING
                    ) {
                      executeGetEmployerNotCompletedSurveys({});
                      executeGetEmployerCredits({});
                      executeGetEmployeesMe({});
                    } else {
                      // Do NOT redirect to /configuration here via
                      // `window.location.href`. A full reload re-runs this
                      // whole init chain from scratch, producing duplicate
                      // /users/me and /organization/config calls. It also
                      // races with the routing guards: the second response
                      // can flip `firstLogin` / `hasViewConfig` mid-render
                      // and bounce the user between /configuration and
                      // /homepage (infinite Navigate loop).
                      // The redirect is already handled declaratively by
                      // `RoutesMustBeConfigured`, which sends users with
                      // empty `firstLogin` to /configuration without a
                      // page reload.
                      executeGetOrganizationConfig({
                        errorSaga: () => {
                          executePostOrganizationConfig({});
                        },
                      });
                    }

                    executeGetAspectForApp({
                      successSaga: ({ response }) => {
                        const responseAspect = response as CallResponseGetAspectForApp;
                        if (responseAspect?.color?.dark) {
                          window.accentDark = responseAspect.color.dark;
                        }
                        if (responseAspect?.color?.light) {
                          window.accentLight = responseAspect.color.light;
                        }
                        if (responseAspect?.color?.dark || responseAspect?.color?.light) {
                          window.dispatchEvent(new CustomEvent('accent-change'));
                        }
                      },
                    });
                    executeGetTemplates({});
                    executeGetUserWarnings({});
                    executeGetUserAuthorize({});
                  },
                  errorSaga: ({ error }) => {
                    if (error.status === UserStatusEnum.Disabled || error.roles.length === 0) {
                      setCustomError(ErrorCodes.USER_DISABLED);
                    }
                  },
                });
              }
              if (responseOrganizations?.data?.length === 0) {
                setCustomError(ErrorCodes.USER_DISABLED);
              }
            },
            errorSaga: () => {
              setCustomError(ErrorCodes.CRITICAL);
            },
          });
        }
      },
      errorSaga: () => {
        setCustomError(ErrorCodes.CRITICAL);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLogged, store, token, invitationId]);

  return { isSuccess, isLoading };
};
